import React, { useState, useMemo, useRef, useEffect } from 'react';

export interface QaCardProps {
  question: string;
  answer: string;
  className?: string;
  index?: number;
  idPrefix?: string;
  isFlipped?: boolean;
  onFlip?: () => void;
}

function seededRandom(seed: number): number {
  const x = Math.sin(seed * 9301 + 49297) * 49297;
  return x - Math.floor(x);
}

function getRandomShadow(index: number): string {
  const shadows = [
    '1px 4px 4px rgba(0,0,0,0.22)',
    '2px 5px 6px rgba(0,0,0,0.24)',
    '1px 4px 5px rgba(0,0,0,0.20)',
    '2px 6px 8px rgba(0,0,0,0.26)',
    '1px 5px 8px rgba(0,0,0,0.22)',
    '0px 4px 10px rgba(0,0,0,0.21)',
    '2px 5px 6px rgba(0,0,0,0.23)',
    '3px 6px 12px rgba(0,0,0,0.25)',
  ];
  const r = seededRandom(index + 42);
  return shadows[Math.floor(r * shadows.length)];
}

const HANDWRITING_FONTS = ["'Playpen Sans', cursive", "'Neucha', cursive", "'Chilanka', cursive"];

function getRandomFont(index: number): string {
  const r = seededRandom(index + 99);
  return HANDWRITING_FONTS[Math.floor(r * HANDWRITING_FONTS.length)];
}

function getScaledFontClass(text: string): string {
  const len = text.length;
  if (len < 65) return 'text-xl lg:text-2xl';
  if (len < 120) return 'text-base lg:text-xl';
  return 'text-sm lg:text-base';
}

function useScrollTrap(backRef: React.RefObject<HTMLDivElement | null>, flipped: boolean) {
  const flippedRef = useRef(false);
  flippedRef.current = flipped;

  useEffect(() => {
    const el = backRef.current;
    if (!el) return undefined;

    const onWheel = (e: WheelEvent) => {
      if (!flippedRef.current) return;
      if (el.scrollHeight > el.clientHeight) {
        e.preventDefault();
        e.stopPropagation();
        el.scrollTop += e.deltaY;
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!flippedRef.current) return;
      if (el.scrollHeight > el.clientHeight) {
        e.stopPropagation();
      }
    };

    el.addEventListener('wheel', onWheel, { passive: false });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    return () => {
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('touchmove', onTouchMove);
    };
  }, [backRef]);
}

export function QaCard({
  question,
  answer,
  className = '',
  index = 0,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
  idPrefix: _idPrefix = 'qa',
  isFlipped,
  onFlip,
}: QaCardProps) {
  const [internalFlipped, setInternalFlipped] = useState(false);
  const [hovered, setHovered] = useState(false);
  const backRef = useRef<HTMLDivElement>(null);

  const flipped = isFlipped !== undefined ? isFlipped : internalFlipped;

  useScrollTrap(backRef, flipped);

  const { rotation, flipDir, shadow, fontFamily } = useMemo(() => {
    const r1 = seededRandom(index + 1);
    const r2 = seededRandom(index + 7);
    return {
      rotation: `${(r1 * 4 - 2).toFixed(1)}deg`,
      flipDir: r2 > 0.5 ? 1 : -1,
      shadow: getRandomShadow(index),
      fontFamily: getRandomFont(index),
    };
  }, [index]);

  const handleFlip = () => {
    if (onFlip) {
      onFlip();
    } else {
      setInternalFlipped(prev => !prev);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleFlip();
    }
  };

  const fontClass = useMemo(() => getScaledFontClass(question), [question]);

  const baseAngle = flipped ? 180 * flipDir : 0;
  const peekAngle = hovered ? 10 * flipDir : 0;
  const flipAngle = baseAngle + peekAngle;

  return (
    <div
      className={`group aspect-square cursor-pointer transition-[filter] duration-500 ${className}`}
      style={{
        transform: `rotate(${rotation})`,
        perspective: '800px',
        filter: `drop-shadow(${shadow})`,
        willChange: 'transform',
      }}
      role="button"
      tabIndex={0}
      aria-label={flipped ? `Answer: ${answer}` : `Question: ${question}`}
      onClick={handleFlip}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div
        className="relative w-full h-full transition-transform duration-500 [transform-style:preserve-3d] ease-[cubic-bezier(0.175,0.885,0.32,1.275)]"
        style={{ transform: `rotateY(${flipAngle}deg)` }}
      >
        {/* Front — Question */}
        <div className="absolute inset-0 flex items-center justify-center p-5 [backface-visibility:hidden] bg-sticky-muted shadow-[0_1px_4px_rgba(0,0,0,0.08)] overflow-hidden">
          <p
            className={`${fontClass} text-slate-800 leading-snug text-center relative z-[1]`}
            style={{ fontFamily }}
          >
            {question}
          </p>

          <svg
            className={`absolute bottom-2 w-5 h-5 text-slate-400/70 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-[1] ${
              flipDir === 1 ? 'left-2' : 'right-2 -scale-x-100'
            }`}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M9 3l-5 5 5 5" />
            <path d="M4 8h11a4 4 0 0 1 0 8H9" />
          </svg>
        </div>

        {/* Back — Answer */}
        <div
          ref={backRef}
          className={`absolute inset-0 [backface-visibility:hidden] bg-sticky-muted shadow-[0_1px_4px_rgba(0,0,0,0.08)] overscroll-contain ${
            flipped ? 'overflow-y-scroll' : 'overflow-hidden pointer-events-none'
          }`}
          style={{ transform: `rotateY(${180 * flipDir}deg)` }}
        >
          <div className="min-h-full flex items-center justify-center p-4">
            <p className="text-sm text-slate-800 leading-relaxed text-center relative z-[1]">
              {answer}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
